// stores/cart.ts
import { defineStore } from 'pinia'
import type { Cart } from '~~/types/cart'

const getLocale = () =>
    (((useNuxtApp() as any).$i18n?.locale?.value) as string) || 'en'

export const useCartStore = defineStore('cart', {
    state: () => ({
        cart: null as Cart | null,
        loading: false,
        error: null as string | null,
        loaded: false,
        lastLocale: null as string | null,
    }),

    getters: {
        items: (s) => s.cart?.items ?? [],
        totalCents: (s) => s.cart?.total_cents ?? 0,
        currency: (s) => s.cart?.currency ?? 'EUR',
        count: (s) => s.cart?.items?.length ?? 0,
        isEmpty: (s) => (s.cart?.items?.length ?? 0) === 0,
    },

    actions: {
        async fetch(opts?: { force?: boolean }) {
            const loc = getLocale()
            if (!opts?.force && this.loaded && this.lastLocale === loc) return

            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Cart>('/api/cart') // $api sets Accept-Language & ?locale
                this.cart = data
                this.lastLocale = loc
            } catch (e: any) {
                this.error = e?.data?.message || e?.message || 'Failed to fetch cart'
            } finally {
                this.loading = false
                this.loaded = true
            }
        },

        async add(productId: number, metadata?: Record<string, any>) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Cart>(`/api/cart/items`, {
                    method: 'POST',
                    body: { product_id: productId, metadata },
                })
                this.cart = data
                this.lastLocale = getLocale()
            } catch (e: any) {
                this.error = e?.data?.message || e?.message || 'Failed to add to cart'
            } finally {
                this.loading = false
            }
        },

        async remove(itemId: number) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Cart>(`/api/cart/items/${itemId}`, { method: 'DELETE' })
                this.cart = data
                this.lastLocale = getLocale()
            } catch (e: any) {
                this.error = e?.data?.message || e?.message || 'Failed to remove item'
            } finally {
                this.loading = false
            }
        },

        async applyCoupon(code: string) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Cart>('/api/cart/coupons', {
                    method: 'POST',
                    body: { code },
                })
                this.cart = data
                this.lastLocale = getLocale()
            } catch (e: any) {
                this.error = e?.data?.message || e?.message || 'Failed to apply coupon'
                throw e
            } finally {
                this.loading = false
            }
        },

        async removeCoupon(couponId: number) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                const data = await $api<Cart>(`/api/cart/coupons/${couponId}`, { method: 'DELETE' })
                this.cart = data
                this.lastLocale = getLocale()
            } catch (e: any) {
                this.error = e?.data?.message || e?.message || 'Failed to remove coupon'
            } finally {
                this.loading = false
            }
        },

        async clear() {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                await $api('/api/cart/clear', { method: 'POST' })
                this.cart = null
                this.lastLocale = getLocale()
                try { await this.fetch({ force: true }) } catch {}
            } catch (e: any) {
                this.error = e?.data?.message || e?.message || 'Failed to clear cart'
                throw e
            } finally {
                this.loading = false
                this.loaded = true
            }
        },

        // convenience for language switch
        async relocalize() {
            if (!this.loaded) return
            const loc = getLocale()
            if (this.lastLocale !== loc) await this.fetch({ force: true })
        },
    },
})
